import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Google sign-in landing page.
 *
 * Reached via redirect from auth.emergentagent.com after a successful
 * Google authentication. The redirect places `#session_id=<one-time-id>`
 * in the URL fragment.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. The /login page sets the redirect using
 * `window.location.origin` so this component never has to know it.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState(null);
  // useRef (synchronous) NOT useState — handles React 18 StrictMode
  // double-effect-firing without producing two backend calls.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      // Direct visit to /auth/callback with no session_id — bounce home
      navigate('/login', { replace: true });
      return;
    }

    const exchange = async () => {
      try {
        // URL PATTERN: Use the same relative-in-production / absolute-in-dev
        // pattern as the AuthContext `api` client (see AuthContext.jsx header).
        // Using REACT_APP_BACKEND_URL raw bakes the preview URL into the
        // production bundle and causes CORS failures on the live domain.
        // See May 2026 fix.
        const apiBase = process.env.NODE_ENV === 'production'
          ? ''
          : (process.env.REACT_APP_BACKEND_URL || '');
        const res = await axios.post(
          `${apiBase}/api/auth/google/session`,
          { session_id: sessionId }
        );
        const { token, user } = res.data;
        loginWithToken(token, user);

        // Strip the session_id from the URL so it doesn't leak via referer
        window.history.replaceState({}, '', '/auth/callback');

        // Land users on the right home page based on their role/user_type
        const dest = (user.role === 'admin' || (user.roles || []).includes('admin'))
          ? '/projects'
          : '/portal';
        navigate(dest, { replace: true });
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 503) {
          toast.error('Google sign-in is currently disabled.');
        } else if (status === 403) {
          toast.error(detail || 'This Google account is not authorised.');
        } else {
          toast.error('Google sign-in failed. Please try email and password.');
        }
        setError(detail || 'Google sign-in failed');
        // Pause briefly so the toast is visible, then return
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      }
    };

    exchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen bg-[#1E2530] flex flex-col items-center justify-center text-white px-6"
      data-testid="auth-callback-screen"
    >
      <div className="flex items-center gap-3">
        <Loader2 className="w-6 h-6 text-[#F5A623] animate-spin" />
        <p className="text-base">
          {error ? 'Redirecting...' : 'Signing you in with Google...'}
        </p>
      </div>
    </div>
  );
}
